import {
  KitchenBoardType,
  KitchenItemType,
  KitchenOrderCardType,
  KitchenOrderDetailType,
  KitchenOrderStatusType,
} from 'features/Order/@types/KitchenMonitoring'
import {
  KitchenCardApiType,
  KitchenDetailApiType,
  KitchenItemApiType,
  KitchenKanbanApiType,
  KitchenModifierApiType,
  KitchenScanApiType,
} from 'features/Order/@types/KitchenMonitoringApi'

export const kitchenApiStatus = (value: string): KitchenOrderStatusType => {
  const status = value.replace('KSP_ORDER_STATUS_', '')
  if (status === 'CHECKING') return 'READY_TO_CHECK'
  if (status === 'COMPLETE') return 'COMPLETED'
  if (status === 'INCOMING' || status === 'PROCESSING') return status
  throw new Error(`Status Kitchen tidak didukung: ${value}`)
}

export const kitchenTimestamp = (value: string) => {
  const timestamp = Number(value)
  if (!timestamp) return ''
  return new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp).toISOString()
}

const isBeverage = (section: string) => /beverage|drink|minuman/i.test(section)

export const kitchenStationLabel = (station: KitchenItemType['station']) => {
  if (station === 'FOOD') return 'Makanan'
  if (station === 'BEVERAGE') return 'Minuman'
  return 'Belum terklasifikasi'
}
const mapStation = (section?: string): KitchenItemType['station'] => {
  if (!section) return 'UNKNOWN'
  if (isBeverage(section)) return 'BEVERAGE'
  return /food|makanan/i.test(section) ? 'FOOD' : 'UNKNOWN'
}

export const kitchenTargetQuantity = (item: {
  quantity: number
  fulfilledQuantity?: number
  isPartialFulfilled?: boolean
}) => (item.isPartialFulfilled ? item.fulfilledQuantity ?? item.quantity : item.quantity)

export const hasCompleteKitchenProgress = (items: KitchenItemType[], action: 'PROCESS' | 'CHECK') =>
  items.length > 0 &&
  items.every((item) => {
    const count = action === 'PROCESS' ? item.processedQuantity : item.checkedQuantity
    return (
      (kitchenTargetQuantity(item) === 0 || item.progressKnown !== false) &&
      count >= kitchenTargetQuantity(item) &&
      item.availability !== 'UNAVAILABLE' &&
      !item.exception &&
      !item.inspectionStatus
    )
  })

export const mapKitchenCard = (
  card: KitchenCardApiType,
  locationId: string,
): KitchenOrderCardType => {
  const sections = card.sections || []
  const food = sections.filter(({ section_name }) => !isBeverage(section_name))
  const drinks = sections.filter(({ section_name }) => isBeverage(section_name))
  const status = kitchenApiStatus(card.ksp_status)
  const progressField = status === 'READY_TO_CHECK' ? 'accepted_items' : 'scanned_items'
  const total = (values: typeof sections, field: 'total_items' | typeof progressField) =>
    values.reduce((sum, section) => sum + (section[field] || 0), 0)
  return {
    id: card.order_id,
    fulfillmentId: card.fulfillment_id,
    locationId,
    orderNumber: card.invoice,
    status,
    dispatchAt:
      card.dispatch_time && Number(card.dispatch_time)
        ? new Date(kitchenTimestamp(card.dispatch_time))
            .toLocaleTimeString('id-ID', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
            .replace('.', ':')
        : '—',
    createdAt: kitchenTimestamp(card.sla_start_ms || card.started_at),
    slaMinutes: card.sla?.max_duration || 0,
    slaDeadlineAt: Number(card.sla_deadline_ms) || undefined,
    version: 0,
    foodQuantity: total(food, 'total_items'),
    beverageQuantity: total(drinks, 'total_items'),
    foodProgressQuantity: total(food, progressField),
    beverageProgressQuantity: total(drinks, progressField),
    processedQuantity: total(sections, 'scanned_items'),
    checkedQuantity: total(sections, 'accepted_items'),
    totalQuantity: card.total_quantity,
  }
}

export const mapKitchenBoard = (
  data: KitchenKanbanApiType,
  locationId: string,
  search = '',
): KitchenBoardType => ({
  locationId,
  columns: (['incoming', 'processing', 'checking'] as const).map((key) => {
    const all = (data[key] || []).map((card) => mapKitchenCard(card, locationId))
    const orders = all.filter(({ orderNumber }) =>
      orderNumber.toLowerCase().includes(search.trim().toLowerCase()),
    )
    const status = {
      incoming: 'INCOMING',
      processing: 'PROCESSING',
      checking: 'READY_TO_CHECK',
    } as const
    return {
      status: status[key],
      total: orders.length,
      orders,
      page: 1,
      pageSize: all.length,
      hasMore: false,
    }
  }),
})

const modifiers = (values: KitchenModifierApiType[] = []) =>
  values.flatMap(({ name, modifier_variants = [] }) =>
    modifier_variants.length
      ? modifier_variants.map((variant) => `${name}: ${variant.name}`)
      : [name],
  )

const mapItem = (item: KitchenItemApiType, scan?: KitchenScanApiType): KitchenItemType => ({
  id: item.line_item_alt_id || item.id,
  adminItemId: item.id,
  sku: item.sku,
  name: item.name,
  image: item.image,
  sectionName: item.section_name || item.kitchen_section,
  station: mapStation(item.section_name || item.kitchen_section),
  quantity: item.quantity,
  fulfilledQuantity: item.fulfill_quantity || 0,
  isPartialFulfilled:
    item.status?.toUpperCase().includes('PARTIAL') ||
    (item.fulfill_quantity > 0 && item.fulfill_quantity < item.quantity),
  processedQuantity: item.crew_scanned_count ?? scan?.crew_scanned_count ?? 0,
  checkedQuantity: item.checker_accepted_count ?? scan?.checker_accepted_count ?? 0,
  progressKnown:
    !!scan || (item.crew_scanned_count !== undefined && item.checker_accepted_count !== undefined),
  modifiers: modifiers(item.modifiers),
  addOns: modifiers(item.add_ons),
  notes: [item.customer_note, item.note].filter(Boolean).join(' · '),
})

export const mapKitchenDetail = (
  detail: KitchenDetailApiType,
  card: KitchenCardApiType,
  locationId: string,
  scans: Map<string, KitchenScanApiType>,
): KitchenOrderDetailType => ({
  id: card.order_id,
  fulfillmentId: card.fulfillment_id,
  locationId,
  orderNumber: detail.invoice || card.invoice,
  status: kitchenApiStatus(card.ksp_status),
  dispatchAt: detail.timeslot?.time_formatted || '—',
  createdAt: kitchenTimestamp(card.started_at || detail.started_at),
  slaMinutes: detail.sla?.max_duration || 0,
  version: 0,
  sections: (card.sections || []).map(({ section_name, complete }) => ({
    sectionName: section_name,
    complete,
  })),
  items: (detail.items || []).map((item) =>
    mapItem(item, scans.get(item.line_item_alt_id || item.id)),
  ),
})
